/* eslint-disable @typescript-eslint/no-empty-function */
import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { toast } from 'ngx-sonner';

let Swal: any = null;
const loadSweetAlert = async () => {
  if (!Swal) {
    try {
      const swalModule = await import('sweetalert2');
      Swal = swalModule.default;
    } catch (error) {
      console.warn('SweetAlert2 not available in this portal', error);
    }
  }
  return Swal;
};

@Injectable({ providedIn: 'root' })
export class NotifyService {
  notifyClearForm = new Subject<boolean>();
  formEvent = new Subject<any>();
  applicationReload = new Subject<boolean>();
  downloadLoading = new Subject<any>();
  notifyFormGroupValidation = new Subject<boolean>();
  interceptorErrorEvent = new Subject<boolean>();
  paymentIframeNotify = new Subject<boolean>();

  private readonly defaultToastConfig = {
    duration: 4000,
    dismissible: true,
  };

 private readonly sweetAlertConfig: any = {
  position: 'top-end',
  toast: true,
  showCloseButton: true,
  showConfirmButton: false,
  iconColor: '#374151',
  timer: 15000,
  customClass: {
    container: 'fixed top-4 right-4 z-[9999]',
    title: 'text-[#1A1A1A] font-medium mb-0 mt-0 ml-2',
    htmlContainer: 'text-[#1A1A1A] text-sm font-thin mb-0 mt-0 ml-2',
    closeButton: 'text-[#1A1A1A]',
    icon: 'text-xs',
  },
};


  private readonly sweetAlertSuccessConfig: any = {
    ...this.sweetAlertConfig,
    background: '#22c55e',
  };

  private readonly sweetAlertErrorConfig: any = {
    ...this.sweetAlertConfig,
    background: '#FEE2E2',
  };

  private readonly sweetAlertWarningConfig: any = {
    ...this.sweetAlertConfig,
    background: '#FEF3C7',
  };

  private readonly sweetAlertInfoConfig: any = {
    ...this.sweetAlertConfig,
    background: '#DBEAFE',
  };


  // notify child component to trigger actions
  notifyClearFormGroup() {
    this.notifyClearForm.next(true);
  }

  notifypaymentIframeNotify() {
    this.paymentIframeNotify.next(true);
  }

  // notify child component to trigger actions
  notifyInterceptorError() {
    this.interceptorErrorEvent.next(true);
  }

  // notify child component to trigger actions
  notifyFormValidation() {
    this.notifyFormGroupValidation.next(true);
  }

  // trigger form event
  triggerFormEvent(event: any) {
    this.formEvent.next(event);
  }

  // trigger Applications reload event
  triggerApplicationReloadEvent(event: boolean) {
    this.applicationReload.next(event);
  }

  // trigger download event
  triggerDownloadEvent(event: any) {
    this.downloadLoading.next(event);
  }

  success(message: string, options: any = {}) {
    toast.success(message, {
      ...this.defaultToastConfig,
      ...options,
    });
  }

  error(message: string, options: any = {}) {
    toast.error(message, {
      ...this.defaultToastConfig,
      ...options,
    });
  }

  private shouldUseNgxSonner(): boolean {
      return true;
  }

  async notifySuccess(message: string): Promise<void> {
    if (this.shouldUseNgxSonner()) {
      toast.success(message, this.defaultToastConfig);
    } else {
      const swal = await loadSweetAlert();
      if (swal) {
        swal.fire({
          icon: 'success',
          html: message,
          ...this.sweetAlertSuccessConfig,
        });
      }
    }
  }

  async notifyError(message: string): Promise<void> {
    if (this.shouldUseNgxSonner()) {
      toast.error(message, this.defaultToastConfig);
    } else {
      const swal = await loadSweetAlert();
      if (swal) {
        swal.fire({
          icon: 'error',
          html: message,
          ...this.sweetAlertErrorConfig,
        });
      }
    }
  }

  async notifyInfo(message: string): Promise<void> {
    if (this.shouldUseNgxSonner()) {
      toast.info(message, this.defaultToastConfig);
    } else {
      const swal = await loadSweetAlert();
      if (swal) {
        swal.fire({
          icon: 'info',
          html: message,
          ...this.sweetAlertInfoConfig,
        });
      }
    }
  }

  async notifyWarning(message: string): Promise<void> {
    if (this.shouldUseNgxSonner()) {
      toast.warning(message, this.defaultToastConfig);
    } else {
      const swal = await loadSweetAlert();
      if (swal) {
        swal.fire({
          icon: 'warning',
          html: message,
          ...this.sweetAlertWarningConfig,
        });
      }
    }
  }

  notifySuccessMessage(title?: string, msgBody?: string): void {
    const message = msgBody || title || 'Success';
    if (this.shouldUseNgxSonner()) {
      this.success(message);
    } else {
      this.notifySuccess(message);
    }
  }

  notifyErrorMessage(title?: string, msgBody?: string): void {
    const message = msgBody || title || 'Error';
    if (this.shouldUseNgxSonner()) {
      this.error(message);
    } else {
      this.notifyError(message);
    }
  }
}
